// Account Client team card — Accounts-Teams-01.
//
// Renders the internal team-member assignments for an Account on
// the overview tab. Source-of-truth for the list is the dedicated
// ``/team-members/`` endpoint via ``useAccountTeamQuery`` so adds /
// patches / removes flow through React-Query and the embedded
// ``client_team`` array on the detail payload becomes the
// first-render snapshot.

import { useMemo, useState } from "react";

import { Button } from "@/design-system";
import { ConfirmDialog } from "@/components/ConfirmDialog";

import { AddAccountTeamMemberModal } from "./AddAccountTeamMemberModal";
import {
  useAccountTeamQuery,
  useAddAccountTeamMemberMutation,
  usePatchAccountTeamMemberMutation,
  useRemoveAccountTeamMemberMutation,
} from "./hooks";
import type {
  AccountTeamMember, AddAccountTeamMemberInput,
} from "./types";


export interface AccountClientTeamCardProps {
  accountId: string;
  /** Initial rows from the Account-detail payload so the card
   * paints synchronously on first mount. The dedicated query takes
   * over once any mutation runs. */
  initialTeam: AccountTeamMember[] | undefined;
}


export function AccountClientTeamCard({
  accountId, initialTeam,
}: AccountClientTeamCardProps) {
  const teamQuery = useAccountTeamQuery(accountId);
  const addMutation = useAddAccountTeamMemberMutation(accountId);
  const patchMutation = usePatchAccountTeamMemberMutation(accountId);
  const removeMutation = useRemoveAccountTeamMemberMutation(accountId);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AccountTeamMember | null>(null);

  const rows: AccountTeamMember[] = useMemo(() => {
    if (teamQuery.data) return teamQuery.data.rows;
    return initialTeam ?? [];
  }, [teamQuery.data, initialTeam]);

  const assignedIds = useMemo(
    () => rows.map((r) => r.membership_id),
    [rows],
  );
  const hasExistingLead = rows.some((r) => r.is_account_lead);

  const onAddSubmit = (input: AddAccountTeamMemberInput) => {
    setModalError(null);
    addMutation.mutate(input, {
      onSuccess: () => setModalOpen(false),
      onError: (err) => setModalError(describeError(err)),
    });
  };

  const onMakeLead = (row: AccountTeamMember) => {
    if (row.is_account_lead) return;
    patchMutation.mutate({
      assignment_id: row.id,
      values: { is_account_lead: true },
    });
  };

  const onConfirmRemove = () => {
    if (!removing) return;
    removeMutation.mutate(removing.id, {
      onSettled: () => setRemoving(null),
    });
  };

  return (
    <section
      data-testid="account-client-team-card"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-raised p-4"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-text-primary">
            Client team
          </h2>
          <p className="mt-1 max-w-2xl text-xs text-text-secondary">
            The internal team members who work on this Account.
            Roles aren&rsquo;t fixed &mdash; the broker decides what
            each person does.
          </p>
        </div>
        <Button
          variant="primary"
          data-testid="account-client-team-add"
          onClick={() => {
            setModalError(null);
            setModalOpen(true);
          }}
        >
          + Add team member
        </Button>
      </header>

      {rows.length === 0 ? (
        <p
          data-testid="account-client-team-empty"
          className="rounded-md border border-dashed border-border-subtle px-3 py-4 text-center text-xs text-text-tertiary"
        >
          Add the internal team members who work on this Account.
        </p>
      ) : (
        <ul
          role="list"
          data-testid="account-client-team-list"
          className="flex flex-col gap-2"
        >
          {rows.map((row) => (
            <TeamRow
              key={row.id}
              row={row}
              onMakeLead={() => onMakeLead(row)}
              onRemove={() => setRemoving(row)}
              pending={
                patchMutation.isPending || removeMutation.isPending
              }
            />
          ))}
        </ul>
      )}

      <AddAccountTeamMemberModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        alreadyAssignedMembershipIds={assignedIds}
        hasExistingLead={hasExistingLead}
        saving={addMutation.isPending}
        error={modalError}
        onSubmit={onAddSubmit}
      />

      <ConfirmDialog
        open={removing !== null}
        title="Remove this team member from the Account?"
        body="This does not deactivate the user or remove their historical work."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={onConfirmRemove}
        onCancel={() => setRemoving(null)}
        testId="account-client-team-remove-confirm"
      />
    </section>
  );
}


function TeamRow({
  row, onMakeLead, onRemove, pending,
}: {
  row: AccountTeamMember;
  onMakeLead: () => void;
  onRemove: () => void;
  pending: boolean;
}) {
  const fullName = row.display_name || row.email;
  return (
    <li
      data-testid={`account-client-team-row-${row.id}`}
      className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-canvas/40 px-3 py-2"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-text-primary">
            {fullName}
          </span>
          {row.is_account_lead && (
            <span
              data-testid={`account-client-team-lead-${row.id}`}
              className="inline-flex items-center rounded-full bg-sage-tint px-1.5 py-0.5 text-[10px] font-medium text-teal"
            >
              Account lead
            </span>
          )}
          {row.is_producer && (
            <span
              data-testid={`account-client-team-producer-${row.id}`}
              className="inline-flex items-center rounded-full bg-gold-tint px-1.5 py-0.5 text-[10px] font-medium text-gold-ink"
            >
              Producer
            </span>
          )}
        </div>
        <span className="truncate text-[11px] text-text-tertiary">
          {row.title ? `${row.title} · ` : ""}{row.email}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        {!row.is_account_lead && (
          <button
            type="button"
            data-testid={`account-client-team-make-lead-${row.id}`}
            onClick={onMakeLead}
            disabled={pending}
            className="font-medium text-teal hover:underline disabled:opacity-50"
          >
            Make Account lead
          </button>
        )}
        <button
          type="button"
          data-testid={`account-client-team-remove-${row.id}`}
          onClick={onRemove}
          disabled={pending}
          className="font-medium text-ember-ink hover:underline disabled:opacity-50"
        >
          Remove
        </button>
      </div>
    </li>
  );
}


function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Couldn't add this team member. Please try again.";
}
